import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Download, RefreshCw, Search } from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

export default function ActivityHistory({ activityView = '' }) {
  const [activityHistory, setActivityHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const downloadButtonRef = useRef(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (err) {
      return {};
    }
  }, []);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchActivityHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/activity-history?limit=250`, { headers: getHeaders() });
      setActivityHistory(res.data || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Activity history load failed');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivityHistory();
  }, [fetchActivityHistory]);

  const downloadActivityHistory = async () => {
    setDownloadLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/activity-history/download`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Activity_History_${formatDate(new Date()).replace(/\//g, '-')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.message || 'Activity history download failed');
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    if (activityView === 'activity-workspace-logs') {
      setSearch('');
    }
    if (activityView === 'activity-download') {
      setSearch('');
      window.setTimeout(() => downloadButtonRef.current?.focus(), 100);
    }
  }, [activityView]);

  const filteredHistory = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activityHistory;
    return activityHistory.filter((item) => (
      [
        item.date,
        item.workspace,
        item.user,
        item.role,
        item.module,
        item.action,
        item.description,
        item.complete_details,
        item.path,
        item.status
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    ));
  }, [activityHistory, search]);

  const stats = [
    { label: 'Total Logs', value: activityHistory.length, color: '#0f766e' },
    { label: 'Success', value: activityHistory.filter((item) => Number(item.status) < 400).length, color: '#047857' },
    { label: 'Failed', value: activityHistory.filter((item) => Number(item.status) >= 400).length, color: '#dc2626' },
    { label: 'Shown', value: filteredHistory.length, color: '#1d4ed8' }
  ];

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <h2 style={{ color: '#1e3a5f', margin: '0 0 8px' }}>Activity History</h2>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
            {user?.role === 'superadmin' ? 'All workspace actions are shown.' : 'Only this admin workspace and users are shown.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="button" onClick={fetchActivityHistory} disabled={historyLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #dbe3ef', padding: '9px 14px', borderRadius: '8px', cursor: historyLoading ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
            <RefreshCw size={15} /> {historyLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" ref={downloadButtonRef} onClick={downloadActivityHistory} disabled={downloadLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: downloadLoading ? '#94a3b8' : '#0f766e', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', cursor: downloadLoading ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
            <Download size={15} /> {downloadLoading ? 'Downloading...' : 'Download'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        {stats.map((item) => (
          <div key={item.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px', borderLeft: `4px solid ${item.color}` }}>
            <span style={{ display: 'block', color: '#64748b', fontSize: '12px', fontWeight: 800, marginBottom: '6px' }}>{item.label}</span>
            <strong style={{ color: item.color, fontSize: '24px' }}>{item.value}</strong>
          </div>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      <section style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <h3 style={{ color: '#1e3a5f', margin: '0 0 4px' }}>Workspace Logs</h3>
            <p style={{ color: '#64748b', margin: 0, fontSize: '13px' }}>Latest {activityHistory.length} activity records</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '260px', background: '#f8fafc', border: '1px solid #dbe3ef', borderRadius: '8px', padding: '8px 10px' }}>
            <Search size={16} color="#64748b" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search activity..." style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '13px', color: '#0f172a' }} />
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1040px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', color: '#334155' }}>
                {['Date', 'Workspace', 'User', 'Role', 'Module', 'Action', 'Complete Details', 'Status'].map((head) => (
                  <th key={head} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', borderBottom: '1px solid #e5e7eb' }}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>Loading activity history...</td>
                </tr>
              ) : filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No activity history found.</td>
                </tr>
              ) : filteredHistory.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', whiteSpace: 'nowrap' }}>{item.date}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569' }}>{item.workspace || '-'}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>{item.user}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569' }}>{item.role}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#0f766e', fontWeight: 'bold' }}>{item.module}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', color: '#1d4ed8', fontWeight: 'bold' }}>{item.action}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#475569', maxWidth: '520px', overflowWrap: 'anywhere' }}>{item.complete_details || item.description || item.path}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: Number(item.status) < 400 ? '#047857' : '#dc2626', fontWeight: 'bold' }}>{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>
    </div>
  );
}
